import {
  categoryAppliesToDraftItemType,
  normalizeCategoryAppliesTo,
  normalizeProductCategoryId,
} from '../domain/categoryTypes';
import type { CategoryRecord } from '../domain/categoryTypes';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  listCategoryChildRowsFromWebCache,
  resolveCategoryRowFromWebCache,
} from './webCatalogMutationResolve';
import {
  getCatalogRamSnapshotCache,
  patchWebCatalogCategorySortOrders,
} from './catalogRamSnapshotCache';
import { submitCommand } from '../../sync/core/submitCommand';
import {
  type CategoryMutationInput,
  type CategoryRow,
  resolveCategoryRowForMutation,
  resolveOrganizationId,
  toSyncPayload,
} from './categoriesRepoShared';
/** Canonical taxonomy: merchants cannot invent categories. */
export const createCategory = async (_input: CategoryMutationInput): Promise<CategoryRecord> => {
  throw new Error(
    'Las categorías son fijas por rubro. Activá o desactivá las que uses; no se pueden crear nombres nuevos.',
  );
};

/** Reject product/service mismatch against `categories.applies_to`. */
export const assertCategoryAllowsItemType = async (
  organizationId: string,
  categoryIdOrSlug: string,
  itemType: 'physical' | 'service',
): Promise<void> => {
  const normalizedCategory = normalizeProductCategoryId(categoryIdOrSlug);
  if (!normalizedCategory) return;

  let appliesToRaw: string | null = null;
  const cached = getCatalogRamSnapshotCache()?.categories.find((row) => {
          if (!row?.id || row.deleted_at) return false;
          if (String(row.organization_id ?? '') !== organizationId) return false;
          const id = String(row.id).trim().toLowerCase();
          const slug = String(row.slug ?? '').trim().toLowerCase();
          const needle = normalizedCategory.trim().toLowerCase();
          return id === needle || slug === needle;
        });
        appliesToRaw = cached?.applies_to != null ? String(cached.applies_to) : null;

  if (appliesToRaw == null) {
    return;
  }

  const appliesTo = normalizeCategoryAppliesTo(appliesToRaw);
  if (categoryAppliesToDraftItemType(appliesTo, itemType)) {
    return;
  }

  throw new Error(
    itemType === 'service'
      ? 'Esta categoría es solo para productos. Elegí otra o cambialá en Categorías.'
      : 'Esta categoría es solo para servicios. Elegí otra o cambialá en Categorías.',
  );
};

export const assertCategorySlugAllowsItemType = assertCategoryAllowsItemType;

/** Canonical taxonomy: only enable/disable is synced; name/attrs are read-only from snapshot. */
export const updateCategory = async (
  categoryId: string,
  input: CategoryMutationInput & { isActive?: boolean },
) => {
  if (typeof input.isActive === 'boolean') {
    return setCategoryActive(categoryId, input.isActive);
  }
  throw new Error(
    'Las categorías son fijas por rubro. Solo podés activarlas o desactivarlas.',
  );
};

async function syncCategoryActiveRows(
  rowsToUpdate: CategoryRow[],
  organizationId: string,
  isActive: boolean,
  timestamp: string,
): Promise<void> {
  for (const row of rowsToUpdate) {
    const rowAppliesTo = normalizeCategoryAppliesTo(
      row.applies_to != null ? String(row.applies_to) : undefined,
    );
    const payloadCategory: CategoryRecord = {
      id: row.id,
      organizationId,
      slug: row.slug,
      name: row.name,
      isActive,
      isRestricted: row.is_restricted === true || row.is_restricted === 1,
      sortOrder: row.sort_order ?? 0,
      color: row.color ?? null,
      appliesTo: rowAppliesTo,
      parentId: row.parent_id ?? null,
      code: row.code ?? null,
      defaultMarginPercent: row.default_margin_percent ?? null,
      description: row.description ?? null,
      imageUrl: row.image_url ?? null,
      createdAt: row.created_at ?? null,
      updatedAt: timestamp,
      deletedAt: null,
    };
    const result = await submitCommand({
      entity: 'category',
      operation: 'update',
      payload: toSyncPayload(payloadCategory, organizationId),
    });
    if (!result.applied) {
      const error = new Error(result.ingestError?.trim() || 'No se pudo actualizar la categoría en el servidor. Reintentá.');
      captureOpsFailure({
        module: 'catalog',
        op: 'catalog.category_write',
        error,
        ingestError: result.ingestError,
        applied: false,
        path: 'web-ram',
        itemId: row.id,
        extra: { categoryId: row.id },
      });
      throw error;
    }
  }
}

/** Orden propio de la org sobre la taxonomía canónica (org_category_settings.sort_order). */
export const reorderCategorySortOrders = async (
  items: { id: string; sortOrder: number }[],
): Promise<void> => {
  if (items.length === 0) return;
  const organizationId = await resolveOrganizationId();
  const updatedAt = new Date().toISOString();
  const { error } = await supabase.rpc('apply_category_command', {
    p_operation: 'reorder',
    p_payload: {
      organizationId,
      operationId: `reorder:${organizationId}:${updatedAt}`,
      items: items.map((item) => ({ id: item.id, sortOrder: item.sortOrder })),
      updatedAt,
    },
  });
  if (error) {
    const err = new Error(error.message || 'No se pudo guardar el orden de categorías.');
    captureOpsFailure({
      module: 'catalog',
      op: 'catalog.category_reorder',
      error: err,
      applied: false,
      path: 'rpc',
    });
    throw err;
  }
  // Sin este patch el snapshot RAM sigue sirviendo el orden viejo hasta el
  // próximo pull — la lista reloaded mostraba las categorías sin moverse.
  patchWebCatalogCategorySortOrders(items);
  void logBusinessAuditEvent('catalog.category.reorder', {
    organization_id: organizationId,
    count: items.length,
  });
};

export const setCategoryActive = async (categoryId: string, isActive: boolean) => {
  const existing = await resolveCategoryRowForMutation(categoryId);
  if (!existing) {
    throw new Error('No encontramos la categoría.');
  }

  const timestamp = new Date().toISOString();
  const organizationId = await resolveOrganizationId();
  const appliesTo = normalizeCategoryAppliesTo(
    existing.applies_to != null ? String(existing.applies_to) : undefined,
  );

  const isParent = existing.parent_id == null;
  let rowsToUpdate: CategoryRow[] = [existing];

  if (isParent && organizationId) {
    const childRows = listCategoryChildRowsFromWebCache(organizationId, categoryId);
    rowsToUpdate = [existing, ...childRows];
  } else if (isActive && existing.parent_id && organizationId) {
    const parentRow = resolveCategoryRowFromWebCache(existing.parent_id, organizationId);
    const parentOff =
      parentRow != null && parentRow.is_active !== true && parentRow.is_active !== 1;
    if (parentOff && parentRow) {
      rowsToUpdate = [existing, parentRow];
    }
  }

  // ADR-017 Fase 4: cola eliminada — sin UPDATE SQLite local ni enqueue
  if (organizationId) {
    await syncCategoryActiveRows(rowsToUpdate, organizationId, isActive, timestamp);
  }

  const category: CategoryRecord = {
    id: existing.id,
    organizationId: organizationId ?? null,
    slug: existing.slug,
    name: existing.name,
    isActive,
    isRestricted: existing.is_restricted === true || existing.is_restricted === 1,
    sortOrder: existing.sort_order ?? 0,
    color: existing.color ?? null,
    appliesTo,
    parentId: existing.parent_id ?? null,
    code: existing.code ?? null,
    defaultMarginPercent: existing.default_margin_percent ?? null,
    description: existing.description ?? null,
    imageUrl: existing.image_url ?? null,
    createdAt: existing.created_at ?? null,
    updatedAt: timestamp,
    deletedAt: null,
  };

  void logBusinessAuditEvent(isActive ? 'catalog.category.activate' : 'catalog.category.deactivate', {
    categoryId,
    organization_id: organizationId ?? undefined,
    cascadedCount: rowsToUpdate.length > 1 ? rowsToUpdate.length - 1 : undefined,
  });

  if (organizationId) {
    const { appendWebCatalogCategoryToCache } = await import('./catalogRamSnapshotCache');
    for (const row of rowsToUpdate) {
      const rowAppliesTo = normalizeCategoryAppliesTo(
        row.applies_to != null ? String(row.applies_to) : undefined,
      );
      appendWebCatalogCategoryToCache({
        id: row.id,
        organization_id: organizationId,
        slug: row.slug,
        name: row.name,
        is_active: isActive,
        is_restricted: row.is_restricted === true || row.is_restricted === 1,
        sort_order: row.sort_order ?? 0,
        applies_to: rowAppliesTo,
        parent_id: row.parent_id ?? null,
        code: row.code ?? null,
        default_margin_percent: row.default_margin_percent ?? null,
        description: row.description ?? null,
        image_url: row.image_url ?? null,
        created_at: row.created_at ?? timestamp,
        updated_at: timestamp,
        deleted_at: null,
      });
    }
  }

  return category;
};
